// ============================================================================
// AUTH CALLBACK PAGE
// Handles OAuth 2.0 redirect after successful Cognito authentication
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { isAuthenticated, loading, authError } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const handleCallback = useCallback(async () => {
    // Wait for auth context to finish loading
    if (loading) {
      return;
    }

    // Check for errors in URL
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    const errorDescription = params.get('error_description');

    if (errorParam) {
      setError(errorDescription || 'Authentication failed');
      console.error('Auth callback error:', errorParam, errorDescription);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      return;
    }

    // If authenticated successfully, redirect to intended page
    if (isAuthenticated) {
      let redirectTo = sessionStorage.getItem('auth_redirect') || '/projects';
      sessionStorage.removeItem('auth_redirect');

      // Don't redirect to auth pages - go to projects instead
      const authPages = ['/login', '/auth/callback', '/unauthorized'];
      if (authPages.includes(redirectTo)) {
        redirectTo = '/projects';
      }

      navigate(redirectTo);
    } else {
      // Auth completed but user is not authenticated — redirect to login
      setError('Authentication completed but your session could not be established. Please try again.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    handleCallback();
  }, [handleCallback]);

  if (!loading && authError && !error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #052831 0%, #063a47 100%)',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          textAlign: 'center',
          maxWidth: '450px',
          width: '100%'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            background: '#fee',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '30px'
          }}>
            !
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#c92a2a',
            marginBottom: '10px'
          }}>
            Authentication Failed
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '20px'
          }}>
            {authError}
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'white',
              background: '#052831',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #052831 0%, #063a47 100%)',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          textAlign: 'center',
          maxWidth: '450px',
          width: '100%'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            background: '#fee',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '30px'
          }}>
            ⚠️
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#c92a2a',
            marginBottom: '10px'
          }}>
            Authentication Failed
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '20px'
          }}>
            {error}
          </p>
          <p style={{
            fontSize: '12px',
            color: '#999'
          }}>
            Redirecting to login page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #052831 0%, #063a47 100%)'
    }}>
      <div style={{
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(255,255,255,0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }} />
        <p style={{
          fontSize: '16px',
          fontWeight: '500'
        }}>
          Completing authentication...
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
